import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import { Scale, Camera, Play, Radio } from 'lucide-react'
import { getAnimalSightings } from '../services/api'
import { CHART_PALETTE, CustomTooltip } from './ChartConfig'
import DeltaBadge from './WeightDelta'
import MediaThumb from './MediaThumb'
import { fallbackToOriginal } from '../utils/media'
import { formatDay, formatEATTime, weightDelta } from '../utils/presence'
import SmartKraalWeighings from './SmartKraalWeighings'

/**
 * The animal's days at the kraal (GET /cattle/{id}/sightings; plan
 * smartkraal-owner-experience B1): "then and now" when two days have a still,
 * a weight sparkline over the weighed days, then one visit card per day, newest
 * first (date, weight with the change against the previous weighed day,
 * confidence, reads, the two stills and the clip's poster). Tapping any picture
 * opens the page's lightbox with that day's media (`onOpen(slides, index)`); a
 * picture whose variant is not built yet opens as the original.
 * The scale's every reading stays reachable under "All scale readings", and is
 * shown on its own for an animal that has readings but no sighting rows yet
 * (the nightly rebuild has not caught up, or its window is older).
 */
export default function KraalTimeline({ animalId, animalName, onOpen }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['animalSightings', animalId],
    queryFn: ({ signal }) => getAnimalSightings(animalId, { limit: 120 }, { signal }),
    enabled: !!animalId,
    staleTime: 5 * 60 * 1000,
  })
  const days = data?.days || []
  const name = animalName || 'this animal'

  // Weighed days, oldest first, for the sparkline.
  const weighed = useMemo(() => days.filter((d) => d.weighed && d.weight_kg != null)
    .map((d) => ({ day: d.day, label: formatDay(d.day, { weekday: false }), weight: d.weight_kg }))
    .reverse(), [days])
  // Then and now: the first and the latest day with a still (different days).
  const withStill = useMemo(() => days.filter((d) => stillOf(d)), [days])
  const thenNow = withStill.length >= 2 && withStill[0].day !== withStill[withStill.length - 1].day
    ? { then: withStill[withStill.length - 1], now: withStill[0] } : null

  if (isLoading || error || !data) return null
  if (days.length === 0) return <SmartKraalWeighings animalId={animalId} />

  return (
    <div className="card p-6" data-testid="kraal-timeline">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <h3 className="text-lg font-medium text-primary-900 flex items-center gap-2">
          <Radio className="h-5 w-5 text-amber-600" />
          At the kraal
          <span className="text-sm font-normal text-earth-500">
            {data.total} day{data.total === 1 ? '' : 's'}
            {data.last_seen ? ` · last ${formatDay(data.last_seen)}` : ''}
          </span>
        </h3>
        <div className="flex items-center gap-3 text-sm">
          <Link to="/cattle/registration" className="text-primary-600 hover:text-primary-800">Registration</Link>
          <Link to="/cattle/smartkraal" className="text-primary-600 hover:text-primary-800">Sync health</Link>
        </div>
      </div>

      {thenNow && <ThenAndNow then={thenNow.then} now={thenNow.now} name={name} />}

      {weighed.length >= 2 && (
        <div className="mb-5" data-testid="weight-sparkline">
          <div className="flex items-center justify-between text-xs text-earth-500 mb-1">
            <span className="flex items-center gap-1"><Scale className="h-3.5 w-3.5" /> Weight at the kraal</span>
            <span>{weighed[0].label} → {weighed[weighed.length - 1].label}</span>
          </div>
          <div className="h-20">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weighed} margin={{ top: 6, right: 8, left: 8, bottom: 2 }}>
                <XAxis dataKey="label" hide />
                <YAxis hide domain={['dataMin - 10', 'dataMax + 10']} />
                <Tooltip content={<CustomTooltip formatter={(v) => `${v} kg`} />} />
                <Line type="monotone" dataKey="weight" name="Weight" stroke={CHART_PALETTE[0]} strokeWidth={2}
                      dot={{ r: 3, fill: CHART_PALETTE[0] }} activeDot={{ r: 5 }} isAnimationActive={false} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <ol className="space-y-3">
        {days.map((d) => <VisitCard key={d.day} day={d} name={name} onOpen={onOpen} />)}
      </ol>
      {days.length < data.total && (
        <p className="mt-3 text-xs text-earth-500">Showing the latest {days.length} of {data.total} days.</p>
      )}

      <details className="mt-5 group">
        <summary className="cursor-pointer text-sm text-primary-700 hover:text-primary-900 select-none">
          All scale readings
        </summary>
        <div className="mt-3">
          <SmartKraalWeighings animalId={animalId} embedded />
        </div>
      </details>
    </div>
  )
}

/** The still to show for a day: camera 1, else camera 2; null without one. */
function stillOf(d) {
  const m = d.media || {}
  return m.cam1_still || m.cam2_still || null
}

/**
 * The lightbox slides for one day's media, with each slide's index by role.
 * `pending` names the roles whose thumbnail failed to load (variant not built
 * yet): those slides carry the original instead of the medium / clip720.
 */
export function slidesForDay(d, name, pending = {}) {
  const m = d.media || {}
  const when = formatDay(d.day)
  const stem = `${(name || 'animal').replace(/[^\w-]+/g, '_')}_${d.day}`
  const slides = []
  const at = {}
  if (m.cam1_still) {
    at.cam1_still = slides.length
    slides.push({ kind: 'image', src: pending.cam1_still ? m.cam1_still.orig : m.cam1_still.medium, orig: m.cam1_still.orig,
                  alt: `${name}, camera 1, ${when}`, caption: `${name} · camera 1 · ${when}`, filename: `${stem}_cam1` })
  }
  if (m.cam2_still) {
    at.cam2_still = slides.length
    slides.push({ kind: 'image', src: pending.cam2_still ? m.cam2_still.orig : m.cam2_still.medium, orig: m.cam2_still.orig,
                  alt: `${name}, camera 2, ${when}`, caption: `${name} · camera 2 · ${when}`, filename: `${stem}_cam2` })
  }
  if (m.cam1_clip) {
    at.cam1_clip = slides.length
    slides.push({ kind: 'video', clip720: pending.cam1_clip ? null : m.cam1_clip.clip720,
                  poster: pending.cam1_clip ? null : m.cam1_clip.poster, orig: m.cam1_clip.orig,
                  caption: `${name} · clip · ${when}`, filename: `${stem}_clip` })
  }
  return { slides, at }
}

function VisitCard({ day: d, name, onOpen }) {
  const m = d.media || {}
  const [pending, setPending] = useState({})
  const delta = weightDelta(d.weight_delta_kg)
  const hasMedia = m.cam1_still || m.cam2_still || m.cam1_clip
  const open = (role) => {
    const { slides, at } = slidesForDay(d, name, pending)
    if (slides.length && onOpen) onOpen(slides, at[role] ?? 0)
  }
  const failed = (role) => () => setPending((p) => ({ ...p, [role]: true }))
  const reads = d.read_count
    ? `${d.read_count} read${d.read_count === 1 ? '' : 's'}${d.first_read_utc ? ` · ${formatEATTime(d.first_read_utc)}${d.last_read_utc && d.last_read_utc !== d.first_read_utc ? `–${formatEATTime(d.last_read_utc)}` : ''} EAT` : ''}`
    : null

  return (
    <li className="rounded-lg border border-earth-200 p-3" data-testid="visit-card">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        <span className="font-medium text-primary-900">{formatDay(d.day)}</span>
        {d.weighed && d.weight_kg != null ? (
          <span className="flex items-center gap-1.5">
            <span className="font-semibold text-gray-900">{d.weight_kg} kg</span>
            {delta && <DeltaBadge delta={delta} />}
          </span>
        ) : (
          <span className="text-earth-500">read, not weighed</span>
        )}
        {d.confidence != null && <ConfidencePill value={d.confidence} />}
        {reads && <span className="text-xs text-earth-500">{reads}</span>}
      </div>
      {hasMedia && (
        <div className="mt-2 flex flex-wrap gap-2">
          {m.cam1_still && <Thumb src={m.cam1_still.thumb} label="Camera 1" onClick={() => open('cam1_still')} onFail={failed('cam1_still')} />}
          {m.cam2_still && <Thumb src={m.cam2_still.thumb} label="Camera 2" onClick={() => open('cam2_still')} onFail={failed('cam2_still')} />}
          {m.cam1_clip && <Thumb src={m.cam1_clip.thumb} label="Clip" video onClick={() => open('cam1_clip')} onFail={failed('cam1_clip')} />}
        </div>
      )}
    </li>
  )
}

/** A visit thumbnail: lazy, the camera glyph showing while the variant is not
 *  built yet (never the 3 MB original in a list). */
function Thumb({ src, label, video = false, onClick, onFail }) {
  return (
    <button type="button" onClick={onClick} title={label}
            className="relative h-20 w-28 overflow-hidden rounded-md bg-earth-100 ring-1 ring-earth-200 hover:ring-primary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
            data-testid={video ? 'visit-clip' : 'visit-still'}>
      <MediaThumb src={src} alt={label} className="h-full w-full" onFail={onFail}
                  glyph={<Camera className="h-6 w-6 text-earth-400" aria-hidden="true" />} />
      {video && (
        <span className="absolute inset-0 flex items-center justify-center">
          <span className="rounded-full bg-black/60 p-2 text-white"><Play className="h-4 w-4" aria-hidden="true" fill="currentColor" /></span>
        </span>
      )}
      <span className="sr-only">{video ? 'Play the clip' : `Open ${label}`}</span>
    </button>
  )
}

function ConfidencePill({ value }) {
  const cls = value >= 90 ? 'bg-primary-100 text-primary-800' : value >= 70 ? 'bg-amber-100 text-amber-800' : 'bg-earth-100 text-earth-500'
  return (
    <span className={`inline-block rounded px-2 py-0.5 text-xs ${cls}`} title="The scale's own confidence in the reading">
      {value}
    </span>
  )
}

/**
 * First still against the latest, under a drag slider: the "then" image is
 * clipped at the handle over the "now" image. The slider is a plain range input
 * laid over the picture (drag with a finger or a mouse, arrows on a keyboard).
 * Two images only, so each may fall back to its original if the medium variant
 * is not built yet.
 */
function ThenAndNow({ then, now, name }) {
  const [pos, setPos] = useState(50)
  const a = stillOf(then)
  const b = stillOf(now)
  const weight = (d) => (d.weighed && d.weight_kg != null ? ` · ${d.weight_kg} kg` : '')
  return (
    <div className="mb-5" data-testid="then-and-now">
      <div className="flex items-baseline justify-between text-xs text-earth-600 mb-1">
        <span><span className="font-semibold text-primary-900">Then</span> · {formatDay(then.day)}{weight(then)}</span>
        <span><span className="font-semibold text-primary-900">Now</span> · {formatDay(now.day)}{weight(now)}</span>
      </div>
      <div className="relative aspect-[4/3] overflow-hidden rounded-lg bg-earth-100 select-none">
        <img src={b.medium} alt={`${name} now`} decoding="async" className="absolute inset-0 h-full w-full object-cover"
             onError={fallbackToOriginal(b.orig)} />
        <img src={a.medium} alt={`${name} then`} decoding="async" className="absolute inset-0 h-full w-full object-cover"
             style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} onError={fallbackToOriginal(a.orig)} />
        <div className="pointer-events-none absolute inset-y-0 w-0.5 bg-white shadow" style={{ left: `calc(${pos}% - 1px)` }} />
        <div className="pointer-events-none absolute top-1/2 h-8 w-8 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-black/40 shadow"
             style={{ left: `${pos}%` }} />
        <span className="pointer-events-none absolute left-2 top-2 rounded bg-black/50 px-1.5 py-0.5 text-xs text-white">Then</span>
        <span className="pointer-events-none absolute right-2 top-2 rounded bg-black/50 px-1.5 py-0.5 text-xs text-white">Now</span>
        <input type="range" min={0} max={100} value={pos} onChange={(e) => setPos(Number(e.target.value))}
               aria-label="Compare then and now" className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0"
               data-testid="then-now-slider" />
      </div>
    </div>
  )
}
